import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Users, 
  MessageSquare, 
  Calendar, 
  CheckCircle2, 
  FileSearch, 
  BadgeDollarSign,
  Clock,
  ExternalLink,
  Video,
  Phone,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MILESTONE_CONFIG = {
  matched: { 
    label: 'Matched', 
    icon: Users, 
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    step: 1 
  },
  initial_conversation: { 
    label: 'Initial Conversation', 
    icon: MessageSquare, 
    color: 'text-purple-500',
    bgColor: 'bg-purple-100',
    step: 2 
  },
  pitch_proposed: { 
    label: 'Pitch Proposed', 
    icon: Calendar, 
    color: 'text-amber-500',
    bgColor: 'bg-amber-100',
    step: 3 
  },
  pitch_session_scheduled: { 
    label: 'Pitch Scheduled', 
    icon: Calendar, 
    color: 'text-orange-500',
    bgColor: 'bg-orange-100',
    step: 4 
  },
  pitch_session_completed: { 
    label: 'Pitch Completed', 
    icon: CheckCircle2, 
    color: 'text-green-500',
    bgColor: 'bg-green-100',
    step: 5 
  },
  due_diligence: { 
    label: 'Due Diligence', 
    icon: FileSearch, 
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-100',
    step: 6 
  },
  investment_decision: { 
    label: 'Investment Decision', 
    icon: BadgeDollarSign, 
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100',
    step: 7 
  }
};

export default function RelationshipMilestones() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const tabActive = isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white';
  const tabInactive = isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300';

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API}/milestones`);
      setMilestones(response.data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPartnerName = (milestone) => {
    return user?.user_id === milestone.founder_id 
      ? milestone.investor_name 
      : milestone.founder_name;
  };

  const getPartnerId = (milestone) => {
    return user?.user_id === milestone.founder_id 
      ? milestone.investor_id 
      : milestone.founder_id;
  };

  const getPartnerRole = (milestone) => {
    return user?.user_id === milestone.founder_id ? 'Investor' : 'Founder';
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'zoom': return <Video size={14} className="text-blue-500" />;
      case 'phone': return <Phone size={14} className="text-green-500" />;
      case 'in_person': return <MapPin size={14} className="text-purple-500" />;
      default: return <Video size={14} className="text-slate-400" />;
    }
  };

  const filteredMilestones = milestones.filter(m => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'active') return !['investment_decision'].includes(m.current_state);
    if (activeFilter === 'scheduled') return m.current_state === 'pitch_session_scheduled';
    if (activeFilter === 'pending') return m.current_state === 'pitch_proposed';
    return m.current_state === activeFilter;
  });

  const getStateStats = () => {
    const stats = {};
    milestones.forEach(m => {
      stats[m.current_state] = (stats[m.current_state] || 0) + 1;
    });
    return stats;
  };

  const stats = getStateStats();

  return (
    <div className={`min-h-screen ${bg}`} data-testid="relationship-milestones-page">
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <Link to="/" className={`inline-flex items-center gap-2 ${textSecondary} hover:${textPrimary} mb-4`}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          <h1 className={`text-3xl font-bold ${textPrimary}`}>Relationship Progress</h1>
          <p className={textSecondary}>Track your investment relationship milestones. Use the Match Profile view to take actions.</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {Object.entries(MILESTONE_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card 
                key={key} 
                className={`p-3 cursor-pointer transition-all ${cardBg} ${activeFilter === key ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setActiveFilter(activeFilter === key ? 'all' : key)}
              >
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded ${config.bgColor}`}>
                    <Icon size={14} className={config.color} />
                  </div>
                  <div>
                    <p className={`text-xs ${textMuted} truncate`}>{config.label}</p>
                    <p className={`text-lg font-bold ${textPrimary}`}>{stats[key] || 0}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${activeFilter === 'all' ? tabActive : tabInactive}`}
          >
            All ({milestones.length})
          </button>
          <button
            onClick={() => setActiveFilter('active')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${activeFilter === 'active' ? tabActive : tabInactive}`}
          >
            Active
          </button>
          <button
            onClick={() => setActiveFilter('scheduled')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${activeFilter === 'scheduled' ? tabActive : tabInactive}`}
          >
            Scheduled
          </button>
          <button
            onClick={() => setActiveFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${activeFilter === 'pending' ? tabActive : tabInactive}`}
          >
            Pending
          </button>
        </div>

        {loading ? (
          <Card className={`p-8 text-center ${cardBg}`}>
            <p className={textSecondary}>Loading...</p>
          </Card>
        ) : filteredMilestones.length === 0 ? (
          <Card className={`p-8 text-center ${cardBg}`}>
            <Users size={48} className={`mx-auto mb-4 ${textMuted}`} />
            <h3 className={`text-lg font-semibold mb-2 ${textPrimary}`}>No relationships yet</h3>
            <p className={`mb-4 ${textSecondary}`}>
              Start matching with {user?.role === 'founder' ? 'investors' : 'founders'} to track your relationship progress.
            </p>
            <Link to="/discovery">
              <Button className="btn-primary">Discover Profiles</Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredMilestones.map((milestone) => {
              const config = MILESTONE_CONFIG[milestone.current_state];
              const Icon = config?.icon || Users;
              const isPendingForMe = milestone.current_state === 'pitch_proposed' && milestone.proposed_by !== user?.user_id;
              const isMyProposal = milestone.current_state === 'pitch_proposed' && milestone.proposed_by === user?.user_id;
              
              return (
                <Card key={milestone.milestone_id} className={`p-5 ${cardBg}`} data-testid={`milestone-${milestone.match_id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Partner Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center">
                        <span className="text-white font-bold text-xl">
                          {getPartnerName(milestone).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className={`font-semibold ${textPrimary}`}>{getPartnerName(milestone)}</h3>
                        <p className={`text-sm ${textMuted}`}>{getPartnerRole(milestone)}</p>
                      </div>
                    </div>

                    {/* Current State Badge */}
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${config?.bgColor || 'bg-slate-100'}`}>
                        <Icon size={14} className={config?.color || 'text-slate-500'} />
                        <span className={`text-sm font-medium ${config?.color || 'text-slate-500'}`}>
                          {config?.label || milestone.current_state}
                        </span>
                      </div>
                      {isPendingForMe && (
                        <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium animate-pulse">
                          Action Required
                        </span>
                      )}
                      {isMyProposal && (
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                          Awaiting Response
                        </span>
                      )}
                    </div>

                    {/* Scheduled Date */}
                    {milestone.confirmed_pitch_slot && (
                      <div className={`flex items-center gap-2 ${textSecondary}`}>
                        <Clock size={14} />
                        <span className="text-sm">{formatDateTime(milestone.confirmed_pitch_slot)}</span>
                        {milestone.meeting_platform && getPlatformIcon(milestone.meeting_platform)}
                        {milestone.meeting_link && (
                          <a href={milestone.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    )}

                    {/* View Profile Action */}
                    <div className="flex gap-2">
                      <Link to={`/profile/${getPartnerId(milestone)}`}>
                        <Button
                          variant={isPendingForMe ? 'default' : 'outline'}
                          size="sm"
                          className={isPendingForMe ? 'btn-primary' : ''}
                          data-testid={`view-profile-${milestone.match_id}`}
                        >
                          {isPendingForMe ? 'Respond Now' : 'View Profile'}
                        </Button>
                      </Link>
                      <Link to={`/messages/${milestone.match_id}`}>
                        <Button variant="ghost" size="sm" className={textMuted}>
                          <MessageSquare size={14} className="mr-1" />
                          Chat
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Session Details - Only show if there's actual scheduled/confirmed data */}
                  {milestone.current_state === 'pitch_session_scheduled' && milestone.confirmed_pitch_slot && (
                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <p className={`text-xs ${textMuted}`}>Scheduled For</p>
                          <p className={`text-sm font-medium ${textPrimary}`}>{formatDateTime(milestone.confirmed_pitch_slot)}</p>
                        </div>
                        {milestone.meeting_platform && (
                          <div>
                            <p className={`text-xs ${textMuted}`}>Meeting Format</p>
                            <p className={`text-sm font-medium ${textPrimary} flex items-center gap-1`}>
                              {getPlatformIcon(milestone.meeting_platform)}
                              {milestone.meeting_platform === 'zoom' ? 'Zoom' : 
                               milestone.meeting_platform === 'phone' ? 'Phone Call' : 
                               milestone.meeting_platform === 'in_person' ? 'In-person' : 'Other'}
                            </p>
                          </div>
                        )}
                        {milestone.meeting_link && (
                          <div>
                            <p className={`text-xs ${textMuted}`}>Meeting Link</p>
                            <a href={milestone.meeting_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1">
                              Join Meeting <ExternalLink size={12} />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Investment Decision Display */}
                  {milestone.investment_decision && (
                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-sm font-medium ${textSecondary}`}>
                        Decision: 
                        <span className={`ml-2 capitalize ${
                          milestone.investment_decision === 'invested' ? 'text-green-500' :
                          milestone.investment_decision === 'passed' ? 'text-red-500' : 'text-amber-500'
                        }`}>
                          {milestone.investment_decision}
                        </span>
                      </p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
